import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, flow, makeObservable, observable, runInAction } from "mobx";
import { isRatable } from "@devowl-wp/real-utils";
import { BaseOptions } from "@devowl-wp/utils";
import { request } from "../utils/request.js";
import { locationRestOptionsPostTypePatch } from "../wp-api/optionsPostType.patch.js";
class OptionStore extends BaseOptions {
    busySettings = false;
    // Implement "others" property in your Assets.php;
    others;
    pureSlug;
    pureSlugCamelCased;
    rootStore;
    get isRatable() {
        return isRatable(this.slug);
    }
    constructor(rootStore){
        super();
        this.rootStore = rootStore;
        this.pureSlug = BaseOptions.getPureSlug(process.env.slug);
        this.pureSlugCamelCased = BaseOptions.getPureSlug(process.env.slug, true);
        makeObservable(this);
        // Object.assign bypasses @observable setters — hydrate others via observable() so nested patches re-render.
        const localized = window[this.pureSlugCamelCased];
        runInAction(()=>{
            const { others, ...rest } = localized;
            Object.assign(this, rest);
            this.others = observable(others);
        });
    }
    updatePostTypeOptions = flow(function*(post_type, { active, fastMode }) {
        this.busySettings = true;
        try {
            const result = yield request({
                location: locationRestOptionsPostTypePatch,
                params: {
                    post_type
                },
                request: {
                    ...active === undefined ? {} : {
                        active: active
                    },
                    ...fastMode === undefined ? {} : {
                        fastMode: fastMode
                    }
                }
            });
            // Update in our store
            if (active !== undefined) {
                this.others.postTypes[post_type].active = active;
            }
            if (fastMode !== undefined) {
                this.others.postTypes[post_type].fastMode = fastMode;
            }
            return result;
        } catch (e) {
            console.log(e);
            throw e;
        } finally{
            this.busySettings = false;
        }
    });
    installAndActivateCustomPostTypeUI = flow(function*() {
        this.busySettings = true;
        const { others: { pluginCptUi, installPluginNonce } } = this;
        const { installed, installUrl, activateUrl } = pluginCptUi;
        const ajaxUrl = window.ajaxurl;
        // Try to install plugin via AJAX request
        if (installed) {
            // Immediate activate the plugin
            try {
                const activateResponse = yield fetch(activateUrl, {
                    credentials: "same-origin"
                });
                if (!activateResponse.ok) {
                    throw new Error(`Plugin activation failed with ${activateResponse.status}`);
                }
                pluginCptUi.active = true;
            } catch (e) {
                // Fallback to usual redirect
                window.location.href = activateUrl;
            }
        } else {
            if (ajaxUrl) {
                try {
                    const installResponse = yield fetch(ajaxUrl, {
                        method: "POST",
                        credentials: "same-origin",
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                            Accept: "application/json, text/javascript, */*; q=0.01"
                        },
                        body: new URLSearchParams({
                            action: "install-plugin",
                            slug: "custom-post-type-ui",
                            _ajax_nonce: installPluginNonce
                        })
                    });
                    if (!installResponse.ok) {
                        throw new Error(`install-plugin failed with ${installResponse.status}`);
                    }
                    const payload = yield installResponse.json();
                    if (!payload.success) {
                        throw new Error("install-plugin was not successful");
                    }
                    pluginCptUi.installed = true;
                    yield this.installAndActivateCustomPostTypeUI();
                } catch (e) {
                    // Fallback to install url (this can happen if FTP is configured to install plugin)
                    window.location.href = installUrl;
                }
            } else {
                window.location.href = installUrl;
            }
        }
    });
    installAndActivateRealCustomPostOrder = flow(function*() {
        this.busySettings = true;
        const { others: { pluginRcpo, installPluginNonce } } = this;
        const { installed, installUrl, activateUrl } = pluginRcpo;
        const ajaxUrl = window.ajaxurl;
        // Try to install plugin via AJAX request
        if (installed) {
            // Immediate activate the plugin
            try {
                const activateResponse = yield fetch(activateUrl, {
                    credentials: "same-origin"
                });
                if (!activateResponse.ok) {
                    throw new Error(`Plugin activation failed with ${activateResponse.status}`);
                }
                pluginRcpo.active = true;
            } catch (e) {
                // Fallback to usual redirect
                window.location.href = activateUrl;
            }
        } else {
            if (ajaxUrl) {
                try {
                    const installResponse = yield fetch(ajaxUrl, {
                        method: "POST",
                        credentials: "same-origin",
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                            Accept: "application/json, text/javascript, */*; q=0.01"
                        },
                        body: new URLSearchParams({
                            action: "install-plugin",
                            slug: "real-custom-post-order",
                            _ajax_nonce: installPluginNonce
                        })
                    });
                    if (!installResponse.ok) {
                        throw new Error(`install-plugin failed with ${installResponse.status}`);
                    }
                    const payload = yield installResponse.json();
                    if (!payload.success) {
                        throw new Error("install-plugin was not successful");
                    }
                    pluginRcpo.installed = true;
                    yield this.installAndActivateCustomPostTypeUI();
                } catch (e) {
                    // Fallback to install url (this can happen if FTP is configured to install plugin)
                    window.location.href = installUrl;
                }
            } else {
                window.location.href = installUrl;
            }
        }
    });
    setTaxnow(taxnow) {
        this.others.taxnow = taxnow;
    }
    setShowLicenseFormImmediate(state) {
        this.others.showLicenseFormImmediate = state;
    }
}
_ts_decorate([
    observable
], OptionStore.prototype, "busySettings", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], OptionStore.prototype, "others", void 0);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        String
    ]),
    _ts_metadata("design:returntype", void 0)
], OptionStore.prototype, "setTaxnow", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Boolean
    ]),
    _ts_metadata("design:returntype", void 0)
], OptionStore.prototype, "setShowLicenseFormImmediate", null);
export { OptionStore };
